import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, Sparkles, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TOOL_LABELS = {
  interlinear: "Strong's Interlinear",
  names: 'Name Meanings & Dictionaries',
  mh: 'Matthew Henry',
  jfb: 'Jamieson-Fausset-Brown',
  clarke: 'Adam Clarke',
  keil: 'Keil & Delitzsch',
};

/**
 * SpotlightAnalyticsCard — SuperAdmin view of the "Now Free" spotlight
 * (`GET /api/analytics/nowfree/summary`): how many users first-opened each
 * newly-free study tool after the pill nudge, split by audience.
 */
const SpotlightAnalyticsCard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/analytics/nowfree/summary`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load spotlight analytics');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const byTool = data?.by_tool || {};
  const byAudience = data?.by_audience || {};
  const total = data?.total || 0;
  const rows = Object.entries(byTool)
    .map(([t, n]) => ({ t, n, label: TOOL_LABELS[t] || t }))
    .sort((a, b) => b.n - a.n);
  const max = Math.max(1, ...rows.map((r) => r.n));

  return (
    <Card className="border-2 border-emerald-300/50 dark:border-emerald-700/40" data-testid="spotlight-analytics-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
            <Sparkles className="w-5 h-5" /> “Now Free” Spotlight — Discovery
          </span>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="spotlight-analytics-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {loading && !data && (
          <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        )}
        {error && <p className="text-red-600 italic" data-testid="spotlight-analytics-error">{error}</p>}
        {data && total === 0 && (
          <p className="text-slate-500 italic" data-testid="spotlight-analytics-empty">
            No opens yet. Once readers tap Strong's or the classic commentaries after seeing the “Now Free” pill, the discovery breakdown appears here.
          </p>
        )}
        {data && total > 0 && (
          <>
            <div className="grid grid-cols-2 gap-3" data-testid="spotlight-analytics-metrics">
              <div className="rounded-lg bg-muted p-3 text-center">
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{total}</p>
                <p className="text-[11px] uppercase tracking-widest text-slate-500">Tool opens</p>
              </div>
              <div className="rounded-lg bg-muted p-3 text-center">
                <p className="text-2xl font-bold">{rows.length}</p>
                <p className="text-[11px] uppercase tracking-widest text-slate-500">Tools discovered</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(byAudience).map(([a, n]) => (
                <Badge key={a} variant="secondary" className="capitalize">{a}: {n}</Badge>
              ))}
            </div>
            <div className="space-y-2" data-testid="spotlight-analytics-tools">
              <p className="text-[10px] uppercase tracking-widest text-slate-400">Which newly-free tool draws the most opens</p>
              {rows.map(({ t, n, label }) => {
                const pct = Math.round((n / max) * 100);
                return (
                  <div key={t} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium truncate pr-2">{label}</span>
                      <span className="text-xs text-slate-500 flex-shrink-0">{n}</span>
                    </div>
                    <div className="w-full h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SpotlightAnalyticsCard;
